import { OpenFeatureProvider } from '@openfeature/react-sdk';
import { ProviderEvents, ProviderStatus } from '@openfeature/web-sdk';
import Box from '@risk-smart/themed-cloudscape-components/box';
import ErrorContent from '@risksmart-app/components/src/error-pages/ErrorContent';
import useRisksmartUser from '@risksmart-app/components/src/hooks/useRisksmartUser';
import Loading from '@risksmart-app/components/src/loading';
import { getEnv } from '@risksmart-app/components/src/utils/environment';
import { isE2eTestOrg } from '@risksmart-app/feature-flags/src/e2e-test-org';
import { parseIgnoreE2eOrgOverride } from '@risksmart-app/feature-flags/src/ignore-e2e-org-override';
import { parseOrgFeatures } from '@risksmart-app/modules/src/index';
import type { Meta } from '@risksmart-app/shared/src/organisation/Meta';
import { toDatadogEnv } from '@risksmart-app/shared/src/utils/datadog-environment';
import * as Sentry from '@sentry/react';
import type { FC, ReactNode } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { ThirdPartyAuth0Context } from 'src/providers/ThirdPartyAuth0Context';

import { useGetOrganisationForFeatureBootstrap } from '../hooks/useGetOrganisation';
import { flagClient } from './client';
import { E2eFlagOverridesContext } from './E2eFlagOverridesContext';
import {
  clearFeatureFlagContext,
  getFeatureFlagBootstrapError,
  setFeatureFlagContext,
} from './openfeature';

interface Props {
  children: ReactNode;
}

type ContextState = 'pending' | 'ready' | 'failed';

/** Safely narrows the organisation metadata fields used by E2E flag overrides. */
const readFeatureMeta = (value: unknown): Meta | undefined => {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return undefined;
  }

  const features =
    'features' in value && typeof value.features === 'string'
      ? value.features
      : undefined;
  const e2eMarker =
    'isE2eTestOrg' in value &&
    (typeof value.isE2eTestOrg === 'boolean' ||
      typeof value.isE2eTestOrg === 'string')
      ? value.isE2eTestOrg
      : undefined;

  return { features, isE2eTestOrg: e2eMarker };
};

/**
 * A provider in one of these states cannot answer a flag evaluation, so the
 * gate refuses to render children. `STALE` is deliberately absent: a stale
 * provider still serves last-known-good cached values, which beats both
 * erroring and silently falling back to the registry default.
 */
const FAILED_STATUSES: ProviderStatus[] = [
  ProviderStatus.ERROR,
  ProviderStatus.FATAL,
];

const PENDING_STATUSES: ProviderStatus[] = [
  ProviderStatus.NOT_READY,
  ProviderStatus.RECONCILING,
];

/**
 * Tracks provider health reactively, so a provider that degrades *after* mount
 * flips the gate rather than leaving live flag reads to quietly resolve
 * defaults.
 */
const useProviderStatus = (): ProviderStatus => {
  const [status, setStatus] = useState<ProviderStatus>(
    () => flagClient.providerStatus
  );

  useEffect(() => {
    const events = [
      ProviderEvents.Ready,
      ProviderEvents.Error,
      ProviderEvents.Stale,
      ProviderEvents.Reconciling,
      ProviderEvents.ContextChanged,
      ProviderEvents.ConfigurationChanged,
    ];
    const handler = (): void => {
      setStatus(flagClient.providerStatus);
    };
    events.forEach((event) => flagClient.addHandler(event, handler));

    // The provider can reach READY between the initial useState and this
    // subscription — resync so the gate never waits on an event already fired.
    handler();

    return () => {
      events.forEach((event) => flagClient.removeHandler(event, handler));
    };
  }, []);

  return status;
};

/**
 * Mounts the OpenFeature React provider and keeps the global evaluation context
 * in sync with the signed-in respondent's organisation.
 *
 * Real organisations resolve through OpenFeature. E2E organisations mirror the
 * web app by resolving from their seeded `Meta.features`, which requires the
 * own-org metadata read that the regression setup updates before each test.
 * Either dependency failing keeps the provider's **fail-closed** behaviour: the
 * portal's only flag is `trpc`, which selects the transport for the whole app,
 * so an unresolved flag has no safe default.
 *
 * The split of responsibility: this gate owns "can we reach a provider at all",
 * `useIsFeatureFlagEnabled` owns "is flag X on". Children therefore never
 * observe an unresolved provider, which is what lets the hook stay a plain
 * value read.
 *
 * `user.orgKey` is guaranteed present here — `ProtectedLayout` gates on
 * `isUserInOrganization(user)`, which requires `org_id`, before rendering this.
 */
export const FeatureFlagProvider: FC<Props> = ({ children }) => {
  const { user } = useRisksmartUser(ThirdPartyAuth0Context);
  const {
    data: organisationData,
    loading: organisationLoading,
    error: organisationError,
  } = useGetOrganisationForFeatureBootstrap();
  const orgKey = user?.orgKey;
  const userId = user?.userId;
  const tenantName = user?.tenantName;
  const meta = readFeatureMeta(organisationData?.auth_organisation[0]?.Meta);
  const features = useMemo(
    () => (meta?.features ? parseOrgFeatures(meta.features) : []),
    [meta?.features]
  );
  const e2eFlagOverrides =
    isE2eTestOrg(meta) &&
    !parseIgnoreE2eOrgOverride(
      getEnv('REACT_APP_FEATURE_FLAG_IGNORE_E2E_ORG_OVERRIDE', true)
    )
      ? features
      : null;

  const [contextState, setContextState] = useState<ContextState>('pending');
  const status = useProviderStatus();
  const bootstrapError = getFeatureFlagBootstrapError();

  useEffect(() => {
    if (!orgKey) {
      // Signed out / identity not yet resolved — drop any stale targeting so
      // flags never evaluate against a previous respondent's org.
      setContextState('pending');
      clearFeatureFlagContext().catch((error) => {
        Sentry.captureException(error, {
          tags: { component: 'feature-flags' },
        });
      });

      return;
    }

    let cancelled = false;
    setContextState('pending');

    setFeatureFlagContext({
      orgId: orgKey,
      userId: userId || undefined,
      tenant: tenantName || undefined,
      // app→prod parity with the server's DD_ENV; `||` (not `??`) so an empty
      // REACT_APP_ENVIRONMENT also falls back.
      env: toDatadogEnv(getEnv('REACT_APP_ENVIRONMENT', true)) || 'unknown',
    })
      .then(() => {
        if (!cancelled) {
          setContextState('ready');
        }
      })
      .catch((error) => {
        Sentry.captureException(error, {
          tags: { component: 'feature-flags' },
        });
        // Fail closed: without a bound targeting key the provider cannot
        // resolve org-scoped rules, so children must not render.
        if (!cancelled) {
          setContextState('failed');
        }
      });

    return () => {
      cancelled = true;
    };
  }, [orgKey, userId, tenantName]);

  // Clear on unmount only — kept separate from the effect above so an identity
  // change never transiently blanks the context mid-session.
  useEffect(() => {
    return () => {
      void clearFeatureFlagContext();
    };
  }, []);

  if (
    bootstrapError !== null ||
    contextState === 'failed' ||
    organisationError !== null ||
    FAILED_STATUSES.includes(status)
  ) {
    return (
      <ErrorContent
        title={'Sorry, we couldn’t make that happen'}
        imgSrc={'/errors/rubiks-cube.png'}
        imgAlt={"Rubik's cube indicating a feature-flag configuration issue"}
      >
        <Box variant={'p'}>{'Please try again later'}</Box>
      </ErrorContent>
    );
  }

  if (
    contextState === 'pending' ||
    organisationLoading ||
    PENDING_STATUSES.includes(status)
  ) {
    return <Loading data-loading-reason={'feature-flags'} />;
  }

  return (
    <E2eFlagOverridesContext.Provider value={e2eFlagOverrides}>
      <OpenFeatureProvider>{children}</OpenFeatureProvider>
    </E2eFlagOverridesContext.Provider>
  );
};
